import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Skeleton, Empty, Tooltip } from "antd";
import Pagination from "@mui/material/Pagination";
import {
  LoginOutlined, SearchOutlined, ReloadOutlined, WarningOutlined, CloseOutlined,
} from "@ant-design/icons";
import { API_BASE_URL } from "../../config";
import { authHeaders } from "../utils/session";
import { parseAgent } from "../utils/userAgent";

/**
 * Who has been signing in to the CMS, and from where.
 *
 * The scope is the server's to decide: a superadmin sees every session, anyone
 * else sees only their own reporting line, and asking for a user outside it is
 * refused with a 403. Nothing is gated here — a check in the browser would only
 * be a second, weaker copy of a rule that is already enforced where it counts.
 * The response says which scope it applied, and that is shown rather than
 * assumed.
 */

const PAGE_SIZE = 20;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const dateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${hh}:${mm}`;
};

const relative = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return "Just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return months < 12 ? `${months}mo ago` : `${Math.floor(months / 12)}y ago`;
};

const chip = (label, { color, bg, border }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 6, padding: "2px 10px",
    borderRadius: 20, fontSize: 11, fontWeight: 700, color, background: bg,
    border: `1px solid ${border}`, whiteSpace: "nowrap",
  }}>
    {label}
  </span>
);

const DOT = (color) => (
  <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }} />
);

/**
 * What a session is doing now.
 *
 * Revoked outranks expired: a session someone signed out of is a different fact
 * from one that merely ran out, and the first is the one worth seeing.
 */
const sessionState = (row) => {
  if (row.revoked_at) return chip("Logged out", { color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" });
  if (Number(row.is_active) === 1) {
    return chip(<>{DOT("#16a34a")} Active session</>, { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" });
  }
  return chip(<>{DOT("#cbd5e1")} Expired</>, { color: "#64748b", bg: "#f8fafc", border: "#e2e8f0" });
};

const thStyle = {
  padding: "13px 16px", textAlign: "left", fontSize: 11.5, fontWeight: 700,
  color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px", whiteSpace: "nowrap",
};
const tdStyle = { padding: "13px 16px", fontSize: 13, color: "#374151", verticalAlign: "middle" };

/**
 * @param {object} [member]  When set, only this member's sessions are asked for.
 */
const TeamLoginActivity = ({ member, onClearMember }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [scope, setScope] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");

  /* One request per pause, not per keystroke. */
  useEffect(() => {
    const t = setTimeout(() => setTerm(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  /* A narrower question is a different result set, so it starts at page one —
     page three of everyone's sessions means nothing once it is one person's. */
  useEffect(() => { setPage(1); }, [term, member?.id]);

  /* Drops an answer that arrives after the reader has moved on. */
  const wanted = useRef("");

  const load = useCallback(async () => {
    const key = `${page}:${term}:${member?.id ?? ""}`;
    wanted.current = key;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/internal-users/login-activity`, {
        params: {
          page,
          limit: PAGE_SIZE,
          ...(term ? { search: term } : {}),
          ...(member?.id ? { user_id: member.id } : {}),
        },
        headers: authHeaders(),
      });
      if (wanted.current !== key) return;
      const body = res.data || {};
      setRows(body.data || []);
      setTotal(body.total ?? (body.data?.length || 0));
      setScope(body.scope || "");
    } catch (err) {
      if (wanted.current !== key) return;
      setRows([]);
      setError(err);
    } finally {
      if (wanted.current === key) setLoading(false);
    }
  }, [page, term, member?.id]);

  useEffect(() => { load(); }, [load]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div style={{
      background: "white", borderRadius: 16, boxShadow: "0 2px 16px rgba(0,0,0,.07)",
      border: "1px solid #f0f0f0", overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        padding: "20px 24px", borderBottom: "2px solid #f5f5f5", display: "flex",
        justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 38, height: 38, background: "linear-gradient(135deg,#2563eb,#3b82f6)",
            borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
            color: "white", fontSize: 16,
          }}>
            <LoginOutlined />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 17, color: "#1f2937" }}>
              {member ? `${member.name}'s sessions` : "Login Activity"}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              {total} session{total !== 1 ? "s" : ""}
              {/* Said rather than assumed — the server decides how much of this
                  the viewer is allowed, and the answer differs by role. */}
              {!member && scope === "all" && " · every CMS user"}
              {!member && scope === "team" && " · your reporting line only"}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {member && (
            <button
              onClick={onClearMember}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, height: 38,
                padding: "0 14px", borderRadius: 9, border: "1px solid #e5e7eb",
                background: "white", color: "#475569", fontSize: 13, fontWeight: 600, cursor: "pointer",
              }}
            >
              <CloseOutlined style={{ fontSize: 11 }} /> Show everyone
            </button>
          )}
          <div style={{ position: "relative", width: 240 }}>
            <SearchOutlined style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: 14 }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, IP..."
              style={{
                width: "100%", height: 38, paddingLeft: 36, paddingRight: 16,
                border: "1px solid #e5e7eb", borderRadius: 9, fontSize: 13,
                outline: "none", background: "white", color: "#374151", boxSizing: "border-box",
              }}
            />
          </div>
          <button
            onClick={load}
            style={{
              width: 38, height: 38, borderRadius: 9, border: "1px solid #e5e7eb",
              background: "white", color: "#475569", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <ReloadOutlined spin={loading} />
          </button>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div style={{ padding: 24 }}>
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} active paragraph={{ rows: 1 }} style={{ marginBottom: 16 }} />)}
        </div>
      ) : error ? (
        <div style={{ padding: "48px 24px", textAlign: "center" }}>
          <WarningOutlined style={{ fontSize: 26, color: "#f59e0b" }} />
          <div style={{ fontWeight: 700, color: "#0f172a", marginTop: 12 }}>
            {error?.response?.status === 403
              ? "You cannot view this user's sessions"
              : "Login activity could not be loaded"}
          </div>
          <div style={{ color: "#64748b", fontSize: 13, marginTop: 4, marginBottom: 18 }}>
            {error?.response?.data?.message
              || (error?.response?.status === 403
                ? "They sit outside your reporting line."
                : error?.message || "Something went wrong.")}
          </div>
          <button
            onClick={load}
            style={{
              display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 18px",
              borderRadius: 10, border: "1px solid #bfdbfe", background: "#eff6ff",
              color: "#2563eb", fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}
          >
            <ReloadOutlined /> Try again
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div style={{ padding: "60px 24px", textAlign: "center" }}>
          <Empty description={(
            <span style={{ color: "#9ca3af" }}>
              {term ? "No sessions match that search." : "No sign-ins recorded yet."}
            </span>
          )} />
        </div>
      ) : (
        <div className="table-responsive">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "linear-gradient(135deg,#f8f9ff 0%,#f1f5f9 100%)", borderBottom: "2px solid #e2e8f0" }}>
                <th style={{ ...thStyle, width: 56, textAlign: "center" }}>#</th>
                <th style={thStyle}>User</th>
                <th style={thStyle}>Role</th>
                <th style={thStyle}>Device</th>
                <th style={thStyle}>IP</th>
                <th style={thStyle}>Signed in</th>
                <th style={thStyle}>Last used</th>
                <th style={thStyle}>State</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const { icon, label } = parseAgent(r.user_agent);
                return (
                  <tr key={r.id ?? idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ ...tdStyle, textAlign: "center", color: "#9ca3af", fontWeight: 600 }}>
                      {(page - 1) * PAGE_SIZE + idx + 1}
                    </td>
                    <td style={{ ...tdStyle, minWidth: 190 }}>
                      <div style={{ fontWeight: 700, color: "#111827" }}>{r.user_name || "—"}</div>
                      {r.email && <div style={{ fontSize: 12, color: "#6b7280", wordBreak: "break-all" }}>{r.email}</div>}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap", color: "#475569" }}>
                      {r.cms_role_name || r.user_role || "—"}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      <Tooltip title={r.user_agent || "No user agent recorded"}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, color: "#334155" }}>
                          <span style={{ color: "#94a3b8" }}>{icon}</span>
                          {label}
                        </span>
                      </Tooltip>
                    </td>
                    <td style={{ ...tdStyle, fontFamily: "ui-monospace,SFMono-Regular,monospace", fontSize: 12, color: "#475569" }}>
                      {r.ip || "—"}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      <Tooltip title={dateTime(r.created_at)}>
                        <span style={{ color: "#334155" }}>{relative(r.created_at)}</span>
                      </Tooltip>
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      {r.last_used_at
                        ? (
                          <Tooltip title={dateTime(r.last_used_at)}>
                            <span style={{ color: "#64748b" }}>{relative(r.last_used_at)}</span>
                          </Tooltip>
                        )
                        : <span style={{ color: "#cbd5e1" }}>—</span>}
                    </td>
                    <td style={tdStyle}>{sessionState(r)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && total > PAGE_SIZE && (
        <div style={{
          padding: "16px 24px", borderTop: "1px solid #f0f0f0", display: "flex",
          justifyContent: "space-between", alignItems: "center", gap: 16,
          flexWrap: "wrap", background: "#fafbff",
        }}>
          <span style={{ fontSize: 12.5, color: "#6b7280" }}>
            Showing <b>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)}</b> of <b>{total}</b>
          </span>
          <Pagination count={pageCount} page={page} onChange={(_, v) => setPage(v)} color="primary" />
        </div>
      )}
    </div>
  );
};

export default TeamLoginActivity;
